<template>
  <b-modal
    ref="editModal"
    :title="isUpdated ? $t('button.update') : $t('button.approve')"
    hide-footer
    size="lg"
    centered
    @hidden="onHidden"
  >
    <validation-observer ref="formValidation" #default="{ invalid }">
      <b-alert variant="info" v-if="true" :show="true">
        <div class="alert-body text-center">{{ $t('summary.hint') }}</div>
      </b-alert>

      <div v-for="field in fields" :key="field.key" class="convo">
        <validation-provider
          v-slot="{ errors }"
          :name="$t(`summary.${field.label}`)"
          :rules="field.rules"
        >
          <div class="inner">
            <label>
              {{ $t(`summary.${field.label}`) }}
            </label>
            <div class="input-group-t">
              <b-form-textarea
                v-model="data[field.key]"
                :placeholder="$t(`summary.${field.label}`)"
                :name="field.key"
                :disabled="field.disabled"
                :rules="field.rules"
                autocomplete="off"
                :class="[errors.length > 0 ? 'is-invalid' : null]"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </div>
            <b-button
              variant="danger"
              size="sm"
              :disabled="!canDelete(field.label)"
              @click="deleteField(field.key)"
            >
              <feather-icon icon="TrashIcon" />
            </b-button>
          </div>
        </validation-provider>
      </div>

      <div class="convo">
        <div class="inner btn-new-msg">
          <label>{{ $t('button.addNew') }}</label>
          <b-button variant="outline" size="sm" @click="addMessage('customer')">
            {{ $t('summary.customerMessage') }}
          </b-button>
          <b-button variant="outline" size="sm" @click="addMessage('merchant')">
            {{ $t('summary.merchantMessage') }}
          </b-button>
        </div>
      </div>

      <b-row class="mt-3">
        <b-col class="text-right">
          <b-button variant="outline-secondary" class="mr-2" @click="hide">
            {{ $t('button.cancel') }}
          </b-button>
          <b-button
            :variant="isUpdated ? 'primary' : 'success'"
            :disabled="invalid || submitting || disabledSubmit"
            :loading="submitting"
            @click="onSubmit"
          >
            <span
              v-if="submitting"
              class="spinner-border spinner-border-sm mr-1"
            />
            {{ isUpdated ? $t('button.update') : $t('button.approve') }}
          </b-button>
        </b-col>
      </b-row>
    </validation-observer>
  </b-modal>
</template>

<script>
  import { ValidationProvider, ValidationObserver } from 'vee-validate';
  import {
    BModal,
    BFormGroup,
    BFormTextarea,
    BRow,
    BCol,
    BButton,
    BFormInput,
    BAlert
  } from 'bootstrap-vue';
  import Repository from '@/repositories/RepositoryFactory';
  import NInput from '@/components/NInput.vue';

  const TopicSummaryRepository = Repository.get('topicSummary');

  export default {
    name: 'ConversationSummaryFormModal',
    components: {
      ValidationProvider,
      ValidationObserver,
      BModal,
      BFormGroup,
      BFormTextarea,
      BRow,
      BCol,
      BButton,
      NInput,
      BFormInput,
      BAlert
    },
    props: {
      isUpdated: {
        type: Boolean,
        required: false,
        default: false
      }
    },
    data() {
      return {
        formData: {
          conversation: '',
          // fullConversation: '',
          topics: {},
          id: null
        },
        submitting: false,
        fields: [],
        data: {},
        initData: {}
      };
    },
    computed: {
      disabledSubmit() {
        const convo = this.getConversation();
        return !convo.includes('#customer#') || !convo.includes('#merchant#');
      },
      disabledDeleted() {
        const convo = this.getConversation();
        const customerCount = (convo.match(/#customer#:/g) || []).length;
        const merchantCount = (convo.match(/#merchant#:/g) || []).length;

        return customerCount === 1 && merchantCount === 1;
      }
    },
    methods: {
      addMessage(sender) {
        console.log(this.data);
        const key = `line${this.fields.length}`;
        const newItem = {
          cols: 12,
          key,
          label: `#${sender}#:`,
          rules: 'required',
          type: 'text'
        };

        this.data[key] = '';
        this.fields.push(newItem);
      },

      canDelete(typeLabel) {
        const label = typeLabel.trim();
        const convo = this.getConversation();
        const customerCount = (convo.match(/#customer#:/g) || []).length;
        const merchantCount = (convo.match(/#merchant#:/g) || []).length;

        if (label === '#customer#:') {
          console.log(label, customerCount > 1);
          return customerCount > 1;
        }
        if (label === '#merchant#:') {
          console.log(label, merchantCount > 1);
          return merchantCount > 1;
        }

        return false;
      },
      getConversation() {
        const lines = Object.keys(this.data).reduce((acc, curr) => {
          const label = this.fields.find(field => field.key === curr)?.label;
          if (label) acc.push(`${label} ${this.data[curr]}`);
          return acc;
        }, []);

        // Join lines with newline, avoiding trailing \n
        return lines.join('\n');
      },
      deleteField(key) {
        this.$bvModal
          .msgBoxConfirm(this.$t('alert.unableToRevertAfterRestore'), {
            title: this.$t('alert.confirmContinueOperation'),
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: this.$t('button.delete'),
            cancelTitle: this.$t('button.cancel'),
            cancelVariant: 'outline-secondary',
            hideHeaderClose: false,
            centered: true
          })
          .then(value => {
            if (value) {
              this.fields = this.fields.filter(field => field.key !== key);
            }
          });
      },
      splitConversation(text) {
        // Split by either #customer#: or #merchant#:
        const parts = text.split(/(#customer#:|#merchant#:)/);

        // Filter out empty strings and map to structured format
        const messages = [];
        for (let i = 0; i < parts.length; i += 1) {
          if (parts[i] === '#customer#:' || parts[i] === '#merchant#:') {
            messages.push({
              type: parts[i],
              message: parts[i + 1]?.trim() || ''
            });
          }
        }

        return messages;
      },
      show(item) {
        this.formData = {
          conversation: item.topics?.conversation || '',
          // fullConversation: item.fullConversation || '',
          topics: item.topics,
          id: item.id
          // conversation: '#customer#: How to transfer money ?\n#merchant#: to transfer money \ngoto account -> settings -> click on transfer with amount you want',
        };

        const lines = this.splitConversation(this.formData.conversation);
        this.fields = lines.map((line, index) => {
          this.data[`line${index}`] = line.message.trim();

          return {
            key: `line${index}`,
            label: line.type ? `${line.type}` : '',
            rules: 'required',
            type: 'text',
            cols: 12
          };
        });

        this.$refs.editModal.show();
      },
      hide() {
        this.$refs.editModal.hide();
      },
      onHidden() {
        this.resetForm();
        this.$emit('hidden');
      },
      resetForm() {
        this.formData = {
          conversation: ''
          // fullConversation: '',
        };
        this.$nextTick(() => {
          if (this.$refs.formValidation) {
            this.$refs.formValidation.reset();
          }
        });
      },
      async onSubmit() {
        const isValid = await this.$refs.formValidation.validate();

        if (!isValid || this.submitting) return;

        this.submitting = true;

        const convo = this.getConversation();

        try {
          const payload = {
            topics: {
              ...this.formData.topics,
              conversation: convo
            },
            statusPushToRag: 'approved',
            ...(!this.isUpdated &&
              this.formData.topics.id && { topicId: this.formData.topics.id })
            // fullConversation: this.formData.fullConversation,
          };

          if (this.isUpdated) {
            await TopicSummaryRepository.updateKnowledgeRecord(
              this.formData.id,
              {
                conversation: convo
              }
            );
          } else {
            await TopicSummaryRepository.updateConversationSummary(
              this.formData.id,
              payload
            );
          }

          this.hide();
          this.$emit('submit', {
            id: this.formData.id,
            ...payload
          });
        } catch (error) {
          console.error('Update failed:', error);
          this.$toast.error(this.$t('message.update_failed'));
        } finally {
          this.submitting = false;
        }
      }
    }
  };
</script>

<style lang="scss" scoped>
  .modal-body {
    padding: 1.5rem;
  }

  .convo {
    display: flex;
    gap: 1rem;
    align-items: center;
    width: 100%;

    & > span {
      width: 100%;
    }

    .input-group-t {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 4px;

      textarea {
        width: 100%;
      }
    }

    .inner {
      display: flex;
      gap: 1rem;
      align-items: center;
      width: 100%;
      margin-top: 1rem;

      label {
        margin: 0;
        min-width: 100px;
      }
    }

    .btn-new-msg {
      button {
        border: 1px dashed #d8d6de;
        padding: 0.9rem;
      }
    }
  }
</style>
